/**
 * DateTime Utilities for consistent timezone handling across the application
 * 
 * All backend times are stored and returned in UTC.
 * This utility helps convert between UTC and local time consistently.
 *
 * @deprecated Superseded by `src/utils/time` (86d42y068 S6). Do not add callers.
 */

export class DateTimeUtils {
	private static readonly DEFAULT_TIMEZONE = 'Australia/Melbourne';

	/**
	 * Get the user's timezone (with fallback to default)
	 */
	static getUserTimezone(): string {
		try {
			return Intl.DateTimeFormat().resolvedOptions().timeZone || this.DEFAULT_TIMEZONE;
		} catch {
			return this.DEFAULT_TIMEZONE;
		}
	}

	/**
	 * Parse a UTC datetime string from the backend and convert to local Date object
	 * @param utcDateString - ISO string from backend (assumed to be UTC)
	 * @returns Date object in local timezone
	 * @deprecated Use `fromUtc()` from `src/utils/time`; this returns Invalid Date for an offset-bearing string.
	 */
	static parseUTCToLocal(utcDateString: string | null | undefined): Date | null {
		if (!utcDateString) return null;

		try {
			// If the string doesn't end with 'Z', append it to indicate UTC
			const normalizedString = utcDateString.endsWith('Z')
				? utcDateString
				: utcDateString + 'Z';

			return new Date(normalizedString);
		} catch (error) {
			console.error('Error parsing UTC date string:', utcDateString, error);
			return null;
		}
	}

	/**
	 * Convert a local Date object to UTC ISO string for sending to backend
	 * @param localDate - Date object in local timezone
	 * @returns UTC ISO string for backend
	 */
	static localToUTCString(localDate: Date | null | undefined): string | null {
		if (!localDate) return null;

		try {
			return localDate.toISOString();
		} catch (error) {
			console.error('Error converting local date to UTC string:', localDate, error);
			return null;
		}
	}

	/**
	 * Format a Date object for display in the user's local timezone
	 * @param date - Date object
	 * @param options - Intl.DateTimeFormatOptions
	 * @returns Formatted string in local timezone
	 */
	static formatLocalDateTime(
		date: Date | string | null | undefined,
		options: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true
		}
	): string {
		if (!date) return '';

		try {
			const dateObj = typeof date === 'string' ? DateTimeUtils.parseUTCToLocal(date) : date;
			if (!dateObj) return '';

			return new Intl.DateTimeFormat('en-AU', {
				...options,
				timeZone: DateTimeUtils.getUserTimezone()
			}).format(dateObj);
		} catch (error) {
			console.error('Error formatting date:', date, error);
			return '';
		}
	}

	/**
	 * Format time only for display
	 * @param date - Date object or UTC string
	 * @param use24Hour - Whether to use 24-hour format
	 * @returns Formatted time string
	 */
	static formatLocalTime(
		date: Date | string | null | undefined,
		use24Hour: boolean = false
	): string {
		if (!date) return '';

		try {
			const dateObj = typeof date === 'string' ? DateTimeUtils.parseUTCToLocal(date) : date;
			if (!dateObj) return '';

			return new Intl.DateTimeFormat('en-AU', {
				hour: '2-digit',
				minute: '2-digit',
				hour12: !use24Hour,
				timeZone: DateTimeUtils.getUserTimezone()
			}).format(dateObj);
		} catch (error) {
			console.error('Error formatting time:', date, error);
			return '';
		}
	}

	/**
	 * Format date only for display
	 * @param date - Date object or UTC string
	 * @returns Formatted date string
	 */
	static formatLocalDate(
		date: Date | string | null | undefined,
		options: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		}
	): string {
		if (!date) return '';

		try {
			const dateObj = typeof date === 'string' ? DateTimeUtils.parseUTCToLocal(date) : date;
			if (!dateObj) return '';

			return new Intl.DateTimeFormat('en-AU', {
				...options,
				timeZone: DateTimeUtils.getUserTimezone()
			}).format(dateObj);
		} catch (error) {
			console.error('Error formatting date:', date, error);
			return '';
		}
	}

	/**
	 * Check if a date is today in the user's timezone
	 * @param date - Date object or UTC string
	 * @returns true if the date is today
	 */
	static isToday(date: Date | string | null | undefined): boolean {
		if (!date) return false;

		try {
			const dateObj = typeof date === 'string' ? DateTimeUtils.parseUTCToLocal(date) : date;
			if (!dateObj) return false;

			const today = new Date();
			const timezone = DateTimeUtils.getUserTimezone();

			const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(today);
			const dateStr = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(dateObj);

			return todayStr === dateStr;
		} catch (error) {
			console.error('Error checking if date is today:', date, error);
			return false;
		}
	}

	/**
	 * Get the start and end of day for a given date in the user's timezone
	 * @param date - Date object or UTC string
	 * @returns Object with start and end Date objects in UTC
	 */
	static getDayBounds(date: Date | string): { start: Date; end: Date } | null {
		try {
			const dateObj = typeof date === 'string' ? DateTimeUtils.parseUTCToLocal(date) : date;
			if (!dateObj) return null;

			// Create start of day in user's timezone
			const year = dateObj.getFullYear();
			const month = dateObj.getMonth();
			const day = dateObj.getDate();

			const startOfDay = new Date(year, month, day, 0, 0, 0, 0);
			const endOfDay = new Date(year, month, day, 23, 59, 59, 999);

			return {
				start: startOfDay,
				end: endOfDay
			};
		} catch (error) {
			console.error('Error getting day bounds:', date, error);
			return null;
		}
	}

	/**
	 * Create a Date object from date and time components in the user's timezone
	 * @param date - Date component
	 * @param hours - Hour (0-23)
	 * @param minutes - Minutes (0-59)
	 * @returns Date object
	 */
	static createLocalDateTime(date: Date, hours: number, minutes: number): Date {
		const result = new Date(date);
		result.setHours(hours, minutes, 0, 0);
		return result;
	}

	/**
	 * Convert a time slot response from backend to local timezone info
	 * @param timeSlot - TimeSlot object from backend
	 * @returns TimeSlot with local display information
	 */
	static convertTimeSlotToLocal(timeSlot: any): any {
		if (!timeSlot || !timeSlot.datetime) return timeSlot;

		const localDate = DateTimeUtils.parseUTCToLocal(timeSlot.datetime);
		if (!localDate) return timeSlot;

		return {
			...timeSlot,
			localDateTime: localDate,
			display_time: DateTimeUtils.formatLocalTime(localDate),
			display_date: DateTimeUtils.formatLocalDate(localDate)
		};
	}

	/**
	 * Get timezone offset string for display (e.g., "UTC+10")
	 * @deprecated Use `offsetLabel()` from `src/utils/time`; this labels every date with today's offset.
	 */
	static getTimezoneOffset(): string {
		try {
			const offset = new Date().getTimezoneOffset();
			const hours = Math.floor(Math.abs(offset) / 60);
			const minutes = Math.abs(offset) % 60;
			const sign = offset <= 0 ? '+' : '-';

			return `UTC${sign}${hours.toString().padStart(2, '0')}${minutes > 0 ? ':' + minutes.toString().padStart(2, '0') : ''}`;
		} catch (error) {
			console.error('Error getting timezone offset:', error);
			return 'UTC+10'; // Default to Melbourne time
		}
	}
}

// Export convenience functions for common operations
export const parseUTCToLocal = DateTimeUtils.parseUTCToLocal;
export const localToUTCString = DateTimeUtils.localToUTCString;
export const formatLocalDateTime = DateTimeUtils.formatLocalDateTime;
export const formatLocalTime = DateTimeUtils.formatLocalTime;
export const formatLocalDate = DateTimeUtils.formatLocalDate;
export const isToday = DateTimeUtils.isToday;